import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isOAuthProvider,
  OAUTH_PROVIDER_LABELS,
  oauthProviderSharesEmail,
  type OAuthProvider,
} from '@shared/constants/index.ts';
import { useAppConfig } from './turnstile.tsx';
import i18n from '../i18n/index.ts';

/**
 * Sign in with Google, Apple, Facebook, Microsoft, LinkedIn, X, Instagram or
 * TikTok.
 *
 * These are plain links, not fetch calls, and deliberately so: the flow is a
 * full-page redirect out to the provider and back, which is what lets the Worker
 * set the HttpOnly state cookie and the session cookie on real navigations. An
 * `<a>` also means the browser's own affordances work — middle-click, "open in
 * new tab", and a visible destination in the status bar.
 *
 * The provider marks are inlined as SVG because the Content-Security-Policy
 * permits no external origins at all (see middleware/security-headers.ts), so
 * there is nowhere to load a logo from.
 */

function startPath(provider: OAuthProvider, options: { mode?: 'link'; returnTo?: string; failTo?: string }): string {
  const params = new URLSearchParams();
  if (options.mode) params.set('mode', options.mode);
  if (options.returnTo) params.set('returnTo', options.returnTo);
  // Where a failure should land. Signing in and signing up are the same redirect,
  // so without this a canceled signup reappears on the sign-in page — out of the
  // flow the visitor chose. Server-validated as an in-app path.
  if (options.failTo) params.set('failTo', options.failTo);
  const query = params.toString();
  return `/api/auth/oauth/${provider}/start${query ? `?${query}` : ''}`;
}

const MARKS: Readonly<Record<OAuthProvider, ReactNode>> = {
  google: (
    <svg viewBox="0 0 18 18" className="h-5 w-5 shrink-0" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.32-1.58-5.03-3.7H1.05v2.34A8.99 8.99 0 0 0 9 18Z"
      />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.94H1.05a9 9 0 0 0 0 8.12l2.92-2.34Z" />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A8.99 8.99 0 0 0 1.05 4.94l2.92 2.34C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  ),
  // Apple's mark is monochrome by design and its guidelines ask for it in a
  // single flat color, so it takes the button's own text color rather than a
  // brand hex — which also keeps it legible if the button's palette changes.
  apple: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09ZM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25Z"
      />
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true" focusable="false">
      <path
        fill="#1877F2"
        d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.95h-1.5c-1.48 0-1.95.92-1.95 1.87v2.25h3.32l-.53 3.49h-2.79V24C19.61 23.1 24 18.1 24 12.07Z"
      />
    </svg>
  ),
  microsoft: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true" focusable="false">
      <path fill="#F25022" d="M2 2h9.4v9.4H2Z" />
      <path fill="#7FBA00" d="M12.6 2H22v9.4h-9.4Z" />
      <path fill="#00A4EF" d="M2 12.6h9.4V22H2Z" />
      <path fill="#FFB900" d="M12.6 12.6H22V22h-9.4Z" />
    </svg>
  ),
  // LinkedIn's mark is the wordmark "in" reversed out of its brand blue. Drawn
  // as one path with the glyph knocked through so it works on any button
  // color, rather than a blue square plus white letters that would vanish on
  // a blue hover state.
  linkedin: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true" focusable="false">
      <path
        fill="#0A66C2"
        d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13ZM7.12 20.45H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0Z"
      />
    </svg>
  ),
  // X's mark is monochrome by brand and its guidelines ask for it in a single
  // flat color, so like Apple's it takes the button's own text color rather
  // than a brand hex — which keeps it legible whichever way the palette goes.
  x: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M18.9 1.5h3.68l-8.04 9.19L24 22.5h-7.41l-5.8-7.58-6.64 7.58H.46l8.6-9.83L0 1.5h7.59l5.24 6.93L18.9 1.5Zm-1.29 18.8h2.04L6.48 3.6H4.29l13.32 16.7Z"
      />
    </svg>
  ),
  // Instagram's glyph is drawn against its gradient, which needs a real
  // <linearGradient> rather than a flat fill. The gradient id is namespaced
  // because this mark can appear more than once on a page — the sign-in row and
  // Settings both render it — and duplicate ids would have every instance
  // resolve to whichever one the document happened to define first.
  instagram: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="oauth-mark-instagram" x1="2" y1="22" x2="22" y2="2" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FFD521" />
          <stop offset="0.35" stopColor="#F50000" />
          <stop offset="0.7" stopColor="#B900B4" />
          <stop offset="1" stopColor="#4B00E0" />
        </linearGradient>
      </defs>
      <path
        fill="url(#oauth-mark-instagram)"
        d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.26.07 1.64.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.26.06-1.64.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.26-.07-1.64-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41 1.26-.06 1.64-.07 4.85-.07Zm0 5.68a4.16 4.16 0 1 0 0 8.32 4.16 4.16 0 0 0 0-8.32Zm0 6.86a2.7 2.7 0 1 1 0-5.4 2.7 2.7 0 0 1 0 5.4Zm5.3-7.02a.97.97 0 1 1-1.94 0 .97.97 0 0 1 1.94 0Z"
      />
    </svg>
  ),
  // TikTok's mark is the note in its two brand colors offset behind each
  // other, with the foreground stroke in the button's own text color so it
  // reads on a light or a dark button alike.
  tiktok: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden="true" focusable="false">
      <path
        fill="#25F4EE"
        d="M9.38 9.6v-.94a7.2 7.2 0 0 0-1-.07A7.13 7.13 0 0 0 5.3 21.86a7.11 7.11 0 0 1 5.1-12.06c.34 0 .67.03 1 .08V9.6Z"
      />
      <path
        fill="#FE2C55"
        d="M15.63 2h-.13a5.5 5.5 0 0 0 .85 2.87A5.52 5.52 0 0 1 15.63 2Zm4.87 4.34a5.5 5.5 0 0 1-1.94-.94v.79a5.5 5.5 0 0 0 1.94 2.35V6.34Z"
      />
      <path
        fill="currentColor"
        d="M18.56 6.19a5.5 5.5 0 0 1-2.06-4.19h-3.1v12.6a2.36 2.36 0 1 1-1.66-2.26V9.19a5.53 5.53 0 1 0 4.77 5.47V8.4a8.6 8.6 0 0 0 5.02 1.61V6.9a5.5 5.5 0 0 1-2.97-.71Z"
      />
    </svg>
  ),
};

export function ProviderMark({ provider }: { provider: OAuthProvider }) {
  return <>{MARKS[provider]}</>;
}

/**
 * The row of provider buttons on the sign-in and registration pages.
 *
 * One button covers both: signing in and signing up with a provider are the same
 * redirect, and which one it turns out to be depends on whether the identity is
 * already known — the visitor should not have to know or care which page they
 * are on. Renders nothing when the deployment has no providers configured, so
 * the password form is not preceded by an empty divider.
 *
 * The one exception to "the same everywhere" is the sign-up variant, which
 * drops X, Instagram and TikTok. Those three report no email address, and an
 * account is created against one, so for somebody with no account yet the
 * button could only take them out to the provider and bring them back refused.
 * On the sign-in page they stay, because there they work: the person who
 * connected one from Settings is signing in with it, which is the whole point
 * of having it. Filtered on `intent` rather than removed from the config
 * response for that reason — the provider is genuinely available, just not as a
 * way to start.
 */
export function SocialSignInButtons({
  /** Where to land after a successful sign-in. Server-validated as in-app. */
  returnTo,
  /** "Sign in with" on the sign-in page, "Sign up with" on registration. */
  intent = 'sign-in',
  /**
   * The page these buttons live on, so a cancellation at the provider comes back
   * here rather than moving the visitor between sign-in and sign-up.
   */
  failTo,
  /**
   * Draw the "or" rule above the buttons rather than below them, for a page
   * that puts them after its own form instead of before it.
   */
  dividerAbove = false,
}: {
  returnTo?: string;
  intent?: 'sign-in' | 'sign-up';
  failTo?: string;
  dividerAbove?: boolean;
}) {
  const { t } = useTranslation();
  const config = useAppConfig();
  const offered = config.data?.oauthProviders ?? [];
  const providers = intent === 'sign-up' ? offered.filter(oauthProviderSharesEmail) : offered;

  // Nothing to show is possible on the sign-up page even when the deployment
  // has providers, if the only ones configured are the three that cannot start
  // an account. The divider must not survive the buttons.
  if (providers.length === 0) return null;

  const labelKey = intent === 'sign-up' ? 'social.signUpWith' : 'social.signInWith';

  const divider = (
    <div className={`${dividerAbove ? 'mb-6' : 'mt-6'} flex items-center gap-3`} aria-hidden="true">
      <span className="h-px flex-1 bg-[color:var(--color-line)]" />
      <span className="text-xs uppercase tracking-wide text-[color:var(--color-muted)]">{t('social.orDivider')}</span>
      <span className="h-px flex-1 bg-[color:var(--color-line)]" />
    </div>
  );

  return (
    <div className={dividerAbove ? 'mt-6' : 'mb-6'}>
      {dividerAbove ? divider : null}
      <div className="flex flex-col gap-2">
        {providers.map((provider) => (
          <a
            key={provider}
            href={startPath(provider, { ...(returnTo ? { returnTo } : {}), ...(failTo ? { failTo } : {}) })}
            className="btn btn-secondary w-full justify-center"
          >
            <ProviderMark provider={provider} />
            <span>{t(labelKey, { provider: OAUTH_PROVIDER_LABELS[provider] })}</span>
          </a>
        ))}
      </div>
      {dividerAbove ? null : divider}
    </div>
  );
}

/** A single "Connect <provider>" link, for Settings. */
export function ConnectProviderLink({
  provider,
  returnTo = '/settings',
}: {
  provider: OAuthProvider;
  returnTo?: string;
}) {
  const { t } = useTranslation();
  return (
    <a href={startPath(provider, { mode: 'link', returnTo, failTo: returnTo })} className="btn btn-secondary">
      <ProviderMark provider={provider} />
      <span>{t('social.connectWith', { provider: OAUTH_PROVIDER_LABELS[provider] })}</span>
    </a>
  );
}

// ---------------------------------------------------------------------------
// Failure wording
// ---------------------------------------------------------------------------

/**
 * The OAuth routes redirect here with `?oauth_error=<code>` rather than a
 * message, so the copy lives with the UI and nothing a provider said is ever
 * reflected into the page. `provider` names the one that was tried, where
 * knowing which makes the sentence useful.
 *
 * Not a component — called from render logic before anything is mounted — so
 * it reaches for the `i18n` singleton directly rather than the `useTranslation`
 * hook, the same pattern `components/schedule/format.ts` uses.
 */
export function oauthErrorMessage(code: string | null, provider: string | null): string | null {
  if (!code) return null;
  // `isOAuthProvider` rather than an `in` check: `in` is true for inherited keys,
  // so `?oauth_provider=__proto__` would resolve to Object.prototype and get
  // interpolated into the sentence below as "[object Object]".
  const name =
    provider && isOAuthProvider(provider) ? OAUTH_PROVIDER_LABELS[provider] : i18n.t('socialErrors.fallbackName');

  switch (code) {
    case 'declined':
      return i18n.t('socialErrors.declined', { provider: name });
    case 'expired':
    case 'state':
      return i18n.t('socialErrors.expiredOrState');
    case 'not_enabled':
      return i18n.t('socialErrors.notEnabled', { provider: name });
    case 'no_email':
      return i18n.t('socialErrors.noEmail', { provider: name });
    // A provider that has no address to share, as against one that has one and
    // did not. Different advice, because "add an email to your TikTok account"
    // is a setting that does not exist. See routes/oauth.ts.
    case 'email_never_shared':
      return i18n.t('socialErrors.emailNeverShared', { provider: name });
    case 'email_unverified':
      return i18n.t('socialErrors.emailUnverified', { provider: name });
    // An account already holds this address and the provider is not connected
    // to it. Sign-in never links on an email match; see routes/oauth.ts.
    case 'account_exists':
      return i18n.t('socialErrors.accountExists', { provider: name });
    case 'signup_unverified':
      return i18n.t('socialErrors.signupUnverified', { provider: name });
    case 'inactive':
      return i18n.t('socialErrors.inactive');
    case 'taken':
      return i18n.t('socialErrors.taken', { provider: name });
    case 'already_connected':
      return i18n.t('socialErrors.alreadyConnected', { provider: name });
    case 'not_signed_in':
      return i18n.t('socialErrors.notSignedIn');
    case 'impersonated':
      return i18n.t('socialErrors.impersonated');
    case 'rate_limited':
      return i18n.t('socialErrors.rateLimited');
    case 'provider':
      return i18n.t('socialErrors.provider', { provider: name });
    case 'server_error':
      return i18n.t('socialErrors.serverError');
    default:
      return i18n.t('socialErrors.default');
  }
}
